import { useAuthMe } from '@h2hspark/auth-ui';
import { Skeleton, useErrorToast } from '@h2hspark/ui';
import { Bell, Check } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useOrganizerFollow, useSetOrganizerFollow } from './api';
import { PROFILE_COPY, signedOutFollowPath } from './constants';

/**
 * Follow an organizer (`MEM-018` AC-5).
 *
 * ## Drawn from Brad's design of 27 October (TW 44540823, Sumit 2026-10-28)
 *
 * A filled purple **Follow** with a bell, on the header card's last row, and *"Hear when they
 * publish an event"* beside it. The design draws one state. AC-5 has two, so once followed the
 * control turns outlined and swaps the bell for a check: the two read differently at a glance.
 * **Neither signed-in label is written here**: both come from the API, because the two strings
 * are the criterion's and a screen that composed them would be a second place their wording lives.
 *
 * It replaced the 10 September transcription from `Member App.dc.html`, a full-width outlined
 * button with the followed state filled.
 *
 * ## What a signed-out visitor sees
 *
 * The same Follow, as a link to the member sign-in that comes back to this organizer's page in
 * the app, where the toggle is. Until 28 October a visitor got a lock-marked sentence with a
 * sign-in link instead (the house rule of `whos-coming-panel.tsx`); Brad's design shows the
 * button signed out, and Sumit agreed. It is still never a disabled button, which says "broken",
 * and never a modal: the visitor came to read about an organizer and can finish doing that.
 *
 * ## The optimism is the server's
 *
 * The mutation writes the response into the cache rather than guessing and reconciling. A member
 * on a phone in a room tapping Follow deserves the label to mean what happened; an optimistic
 * flip that silently reverts on a failed request is worse than a moment's wait, and the failure
 * surfaces as a toast rather than as a button that quietly disagrees with the database.
 */
export function FollowOrganizerCard({ slug }: { readonly slug: string }): React.ReactElement {
  const { user, loading } = useAuthMe();
  const signedIn = user !== null && user !== undefined;

  const state = useOrganizerFollow(slug, { enabled: signedIn });
  const setFollow = useSetOrganizerFollow(slug);
  const showError = useErrorToast();

  React.useEffect(() => {
    if (setFollow.error !== null) {
      showError(setFollow.error, 'We could not update whether you follow this organizer.');
    }
  }, [setFollow.error, showError]);

  if (loading && !signedIn) {
    return <Skeleton className="h-11 w-40 rounded-pill" />;
  }

  if (!signedIn) {
    // The same Follow a member sees, as a link: sign in, and come back to this organizer's page
    // in the app, where the toggle is (`MEM-018` AC-5, TW 44540823, Sumit 2026-10-28).
    return (
      <FollowRow>
        <Link to={signedOutFollowPath(slug)} className={FOLLOW_IDLE_CLASSES}>
          <Bell className="h-4 w-4" aria-hidden />
          {PROFILE_COPY.signedOutFollowLabel}
        </Link>
      </FollowRow>
    );
  }

  if (state.isPending || state.data === undefined) {
    return <Skeleton className="h-11 w-40 rounded-pill" />;
  }

  const following = state.data.following;

  return (
    <FollowRow>
      <button
        type="button"
        // `aria-pressed` rather than two buttons: it is one control in two states, which is what
        // AC-5's "toggles between" describes, and a screen reader is told the state rather than
        // left to infer it from a changed label.
        aria-pressed={following}
        disabled={setFollow.isPending}
        onClick={() => {
          setFollow.mutate({ following: !following });
        }}
        className={following ? FOLLOW_ON_CLASSES : FOLLOW_IDLE_CLASSES}
      >
        {following ? (
          <Check className="h-4 w-4" aria-hidden />
        ) : (
          <Bell className="h-4 w-4" aria-hidden />
        )}
        {state.data.label}
      </button>
    </FollowRow>
  );
}

/**
 * Follow and the design's caption beside it (Brad's design of 27 October, `MEM-018` AC-1): the
 * control sits on the header card's last row, opposite the organizer's links, so it is as wide as
 * its label rather than the full width it took when it had a column of its own.
 */
function FollowRow({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {children}
      <span className="text-xs text-muted-foreground">{PROFILE_COPY.followCaption}</span>
    </div>
  );
}

const FOLLOW_BASE_CLASSES =
  'inline-flex items-center justify-center gap-[7px] rounded-pill border-[1.5px] border-brand px-5 py-2.5 font-brand text-sm font-bold transition-colors disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';
/** Not following: the design's filled purple Follow. */
const FOLLOW_IDLE_CLASSES = `${FOLLOW_BASE_CLASSES} bg-brand text-white hover:brightness-110`;
/** Following: outlined, so the two states read differently at a glance. */
const FOLLOW_ON_CLASSES = `${FOLLOW_BASE_CLASSES} bg-card text-brand`;
